import React, { useCallback, useEffect, useState } from 'react';
import { useI18n } from '../../context/i18n';
import { playsTranslations } from './playsCopy';
import { fetchEntitlements, changePacks } from './playsApi';
import './PlaysMeter.css';

export function PlayUsagePolicy({ type = null, compact = false }) {
  const typeLead = type === 'one_time'
    ? 'This one-time Play counts once when you first integrate it or download its CSV.'
    : type === 'always_on'
      ? 'This always-on Play counts once when you first integrate it or download its CSV.'
      : 'A Play counts once when you first integrate it or download its CSV.';
  const repeatCopy = type === 'always_on'
    ? 'During the same billing cycle, sync, retry or download this same Play as often as you need without using another Play. If it remains active in a new cycle, it counts once in that new cycle.'
    : type === 'one_time'
      ? 'During the same billing cycle, retrying or downloading this same Play again does not use another Play. Using it again in a new cycle counts once in that new cycle.'
      : 'During the same billing cycle, syncing, retrying or downloading the same Play again does not use another Play. In a new cycle, an always-on Play that remains active or a one-time Play used again counts once in that new cycle.';

  return (
    <p className={`play-usage-policy${compact ? ' play-usage-policy--compact' : ''}`}>
      {typeLead} {repeatCopy} Pausing or archiving it does not return that use until your next billing cycle.
    </p>
  );
}

// Persistent meter for the Plays area. Consumption comes exclusively from
// /billing/entitlements. `always_on_used_this_cycle` is intentionally distinct
// from `always_on_active`: pausing/archiving changes the latter but must not
// return a consumed Play before the next billing cycle.
export default function PlaysMeter({ token, onNavigateToPricing, refreshKey, entitlements: entitlementsProp }) {
  const { language } = useI18n();
  const copy = playsTranslations[language]?.pricing || playsTranslations.en.pricing;
  const meterCopy = playsTranslations[language]?.meter || playsTranslations.en.meter;

  const [showPolicy, setShowPolicy] = useState(false);
  const [entitlements, setEntitlements] = useState(entitlementsProp || null);
  const [isLoading, setIsLoading] = useState(!entitlementsProp);
  const [error, setError] = useState('');
  const [isAddingPack, setIsAddingPack] = useState(false);
  const [packError, setPackError] = useState('');

  const handleAddPack = useCallback(async () => {
    if (!token || !entitlements) return;
    const current = entitlements.play_packs ?? 0;
    setIsAddingPack(true);
    setPackError('');
    try {
      // Same checkout mechanics as selecting a plan: the backend returns a
      // Shopify confirmationUrl where the merchant approves the new total.
      const result = await changePacks({ token, playPacks: current + 1 });
      if (result?.confirmationUrl) {
        window.location.assign(result.confirmationUrl);
        return;
      }
      setPackError('Could not start the Play Pack update. Please try again.');
    } catch (err) {
      setPackError(err?.message || 'Could not start the Play Pack update. Please try again.');
    } finally {
      setIsAddingPack(false);
    }
  }, [token, entitlements]);

  const load = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await fetchEntitlements({ token });
      setEntitlements(data);
    } catch (err) {
      setError(err?.message || 'Could not load your Plays usage.');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (entitlementsProp) {
      setEntitlements(entitlementsProp);
      setIsLoading(false);
      return;
    }
    load();
  }, [load, refreshKey, entitlementsProp]);

  if (isLoading && !entitlements) {
    return <div className="plays-meter plays-meter-loading">Loading Plays usage...</div>;
  }

  if (error && !entitlements) {
    return <div className="plays-meter plays-meter-error">{error}</div>;
  }

  if (!entitlements) return null;

  const used = entitlements.one_time_used_this_cycle ?? 0;
  const total = entitlements.one_time_total;
  const active = entitlements.always_on_active ?? 0;
  const alwaysOnUsed = entitlements.always_on_used_this_cycle;
  const alwaysOnTotal = entitlements.always_on_total;
  // Backward-compatible and semantically safe: older backends only know the
  // operational `always_on_active`, so keep their "running" meter wording.
  // Never relabel active as consumed.
  const meterText = alwaysOnUsed == null
    ? (entitlements.meter || meterCopy.buildLegacy(used, total == null ? 'Unlimited' : total, active))
    : (entitlements.meter || meterCopy.build(
        used,
        total == null ? 'Unlimited' : total,
        alwaysOnUsed,
        alwaysOnTotal == null ? 'Unlimited' : alwaysOnTotal,
      ));
  // `founding_active` = inside the 6-month founding window (renamed from
  // founding_unlimited in backend commit e00bf7b).
  const isFounding = Boolean(entitlements.founding && entitlements.founding_active);
  const overCapacity = entitlements.over_capacity;
  // Paid plans only, and only while under the per-plan pack cap.
  const packs = entitlements.play_packs ?? 0;
  // max_packs: null = unlimited (Accelerate), number = cap, missing = none.
  const maxPacks = entitlements.max_packs;
  const canAddPack = entitlements.plan !== 'free'
    && (maxPacks === null || (maxPacks > 0 && packs < maxPacks));

  return (
    <div className="plays-meter">
      <div className="plays-meter-row">
        <span className="plays-meter-text">{meterText}</span>
        {isFounding && <span className="plays-meter-founding-badge">Founding</span>}
        {canAddPack && (
          <button
            type="button"
            className="plays-meter-upgrade"
            onClick={handleAddPack}
            disabled={isAddingPack}
          >
            {isAddingPack ? copy.processing : copy.addPack}
          </button>
        )}
        {onNavigateToPricing && (
          <button type="button" className="plays-meter-upgrade" onClick={onNavigateToPricing}>
            {copy.selectPlan === 'Choose plan' ? 'Manage plan' : copy.selectPlan}
          </button>
        )}
      </div>
      <button
        type="button"
        className="plays-meter-policy-toggle"
        onClick={() => setShowPolicy((v) => !v)}
        aria-expanded={showPolicy}
      >
        How usage works
        <span className="material-symbols-outlined plays-meter-policy-chevron">
          {showPolicy ? 'expand_less' : 'expand_more'}
        </span>
      </button>
      {showPolicy && <PlayUsagePolicy compact />}
      {packError && (
        <div className="plays-meter-error" role="alert">{packError}</div>
      )}
      {overCapacity && (
        <div className="plays-meter-over-capacity" role="status">
          {`You have ${overCapacity.active} of ${overCapacity.allowed} always-on Plays running. That is over your current plan; existing Plays keep running.`}
        </div>
      )}
    </div>
  );
}
